import React, { useEffect } from 'react';
import {
    XCircle,
    Brain,
} from 'lucide-react';
import { marked } from 'marked';

const ViewEvaluationModal = ({ isOpen, onClose, playerName, evaluationData, onRunNew }) => {
    // Lock body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    // Handle backdrop click to close
    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    if (!isOpen) return null;
    console.log('ViewEvaluationModal received data:', evaluationData);
    if (!evaluationData) {
        return (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 cursor-pointer" onClick={handleBackdropClick}>
                <div className="bg-[#0A0A0A] rounded-[14px] shadow-xl p-6 w-full max-w-5xl border border-[#1A1A1A] cursor-default" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-bold text-[#F5F5F5]">Evaluation for {playerName}</h3>
                        <button
                            className="text-[#999999] hover:text-[#F5F5F5] transition-colors"
                            onClick={onClose}
                        >
                            <XCircle size={20} />
                        </button>
                    </div>

                    <div className="py-8 text-center text-[#555555]">
                        No evaluation data available for this player.
                    </div>

                    <div className="flex justify-end space-x-3 mt-4">
                        <button
                            className="px-4 py-2 bg-[#E8B800] hover:bg-[#F0C800] text-[#0A0A0A] font-medium rounded-lg flex items-center transition-colors"
                            onClick={onRunNew}
                        >
                            <Brain size={16} className="mr-2" />
                            <span>Run New Evaluation</span>
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const renderMarkdown = (content) => {
        if (!content) return null;

        return (
            <div
                dangerouslySetInnerHTML={{
                    __html: marked(content, { breaks: true })
                }}
                className="prose prose-sm prose-invert max-w-none text-[#999999]"
            />
        );
    };

    const getScoreColor = (score) => {
        if (!score || !score.includes('/')) return 'text-[#555555]';

        try {
            const numScore = parseInt(score.split('/')[0]);

            if (numScore >= 75) return 'text-green-500';
            if (numScore >= 60) return 'text-green-400';
            if (numScore >= 45) return 'text-[#E8B800]';
            return 'text-red-500';
        } catch (e) {
            console.error("Error parsing score:", e);
            return 'text-[#555555]';
        }
    };

    // Parse evaluation data
    let parsedData = null;
    let rawData = null;

    try {
        // Step 1: First try to understand the structure
        console.log('Evaluation data type:', typeof evaluationData);

        if (typeof evaluationData === 'string') {
            console.log('Evaluation data is a string, trying to parse');
            parsedData = JSON.parse(evaluationData);
            rawData = evaluationData;
        } else if (evaluationData && typeof evaluationData === 'object') {
            console.log('Evaluation data is an object');
            rawData = JSON.stringify(evaluationData, null, 2);

            // Direct match for the API response format from the TP evaluation endpoint
            if (evaluationData.output && typeof evaluationData.output === 'string') {
                console.log('Found direct output string property, trying to parse');
                try {
                    parsedData = JSON.parse(evaluationData.output);
                } catch (innerError) {
                    console.error("Error parsing direct output:", innerError);
                    parsedData = {
                        raw_content: evaluationData.output,
                        parsed: false
                    };
                }
            }
            // If it has agent_output field with output property
            else if (evaluationData.agent_output && typeof evaluationData.agent_output.output === 'string') {
                console.log('Found agent_output.output string, trying to parse');
                try {
                    parsedData = JSON.parse(evaluationData.agent_output.output);
                } catch (innerError) {
                    console.error("Error parsing agent_output.output:", innerError);
                    // If we can't parse it, use it as a string directly
                    parsedData = {
                        raw_content: evaluationData.agent_output.output,
                        parsed: false
                    };
                }
            }
            // Check if it already has the expected fields
            else if (evaluationData.statistical_analysis ||
                evaluationData.character_evaluation ||
                evaluationData.events) {
                console.log('Found expected evaluation fields directly');
                parsedData = evaluationData;
            }
            // Check if the object itself might be what we need
            else if (Object.keys(evaluationData).length > 0) {
                console.log('Using the object itself as parsed data');
                parsedData = evaluationData;
            }
        }

        // If we still don't have parsed data, try to use the raw evaluation data
        if (!parsedData && evaluationData) {
            console.log('Fallback: using the data as-is');
            parsedData = {
                raw_content: typeof evaluationData === 'object' ?
                    JSON.stringify(evaluationData, null, 2) :
                    String(evaluationData),
                parsed: false
            };
        }
    } catch (error) {
        console.error("Error processing evaluation data:", error);
        // Create a fallback data structure with the raw data
        parsedData = {
            error: error.message,
            raw_content: rawData || String(evaluationData),
            parsed: false
        };
    }

    // If we still have no parsedData, show an error
    if (!parsedData) {
        return (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 cursor-pointer" onClick={handleBackdropClick}>
                <div className="bg-[#0A0A0A] rounded-[14px] shadow-xl p-6 w-full max-w-5xl border border-[#1A1A1A] cursor-default" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-bold text-[#F5F5F5]">Evaluation for {playerName}</h3>
                        <button
                            className="text-[#999999] hover:text-[#F5F5F5] transition-colors"
                            onClick={onClose}
                        >
                            <XCircle size={20} />
                        </button>
                    </div>

                    <div className="py-8 text-center text-[#555555]">
                        Error loading evaluation data. The data may be corrupted or in an unsupported format.
                    </div>

                    <div className="flex justify-end space-x-3 mt-4">
                        <button
                            className="px-4 py-2 bg-[#E8B800] hover:bg-[#F0C800] text-[#0A0A0A] font-medium rounded-lg flex items-center transition-colors"
                            onClick={onRunNew}
                        >
                            <Brain size={16} className="mr-2" />
                            <span>Run New Evaluation</span>
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 cursor-pointer" onClick={handleBackdropClick}>
            <div className="bg-[#0A0A0A] rounded-[14px] shadow-xl px-6 pb-6 w-full max-w-5xl max-h-[90vh] overflow-auto border border-[#1A1A1A] cursor-default" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center pt-6 mb-4 sticky top-0 bg-[#0A0A0A] pb-2 border-b border-[#1A1A1A]">
                    <div>
                        <h3 className="text-lg font-bold text-[#F5F5F5]">Evaluation for {playerName}</h3>
                        {evaluationData && evaluationData.created_at && (
                            <p className="text-xs text-[#555555]">
                                Evaluation created: {new Date(evaluationData.created_at).toLocaleString()}
                            </p>
                        )}
                    </div>
                    <div className="flex space-x-2">
                        <button
                            className="px-4 py-2 bg-[#E8B800] hover:bg-[#F0C800] text-[#0A0A0A] font-medium rounded-lg flex items-center transition-colors"
                            onClick={onRunNew}
                        >
                            <Brain size={16} className="mr-2" />
                            <span>Run New Evaluation</span>
                        </button>
                        <button
                            className="text-[#999999] hover:text-[#F5F5F5] transition-colors"
                            onClick={onClose}
                        >
                            <XCircle size={20} />
                        </button>
                    </div>
                </div>

                <div className="space-y-6">
                    {/* Handle raw content fallback */}
                    {parsedData.parsed === false && parsedData.raw_content && (
                        <div className="bg-[#111111] rounded-[14px] p-4 border border-[#1A1A1A]">
                            <h3 className="text-lg font-semibold mb-3 text-[#F5F5F5]">Evaluation Data</h3>
                            <div className="prose prose-sm prose-invert max-w-none text-[#999999]">
                                {typeof parsedData.raw_content === 'string' && parsedData.raw_content.trim().startsWith('{') ? (
                                    // If it looks like JSON, render it in a code block
                                    <pre className="bg-[#0A0A0A] p-4 overflow-auto rounded text-sm text-[#999999]">
                                        {parsedData.raw_content}
                                    </pre>
                                ) : (
                                    // Otherwise render as markdown
                                    renderMarkdown(parsedData.raw_content)
                                )}
                            </div>
                        </div>
                    )}

                    {/* Standard fields display when available */}
                    {parsedData.parsed !== false && (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Statistical Analysis */}
                                {parsedData.statistical_analysis && (
                                    <div className="bg-[#111111] rounded-[14px] p-4 border border-[#1A1A1A]">
                                        <h3 className="text-lg font-semibold mb-3 text-[#F5F5F5]">Statistical Analysis</h3>
                                        <div className="prose prose-sm prose-invert max-w-none text-[#999999]">
                                            {renderMarkdown(parsedData.statistical_analysis)}
                                        </div>
                                    </div>
                                )}

                                {/* Character Evaluation */}
                                {parsedData.character_evaluation && (
                                    <div className="bg-[#111111] rounded-[14px] p-4 border border-[#1A1A1A]">
                                        <h3 className="text-lg font-semibold mb-3 text-[#F5F5F5]">Character Assessment</h3>
                                        <div className="prose prose-sm prose-invert max-w-none text-[#999999]">
                                            {renderMarkdown(parsedData.character_evaluation)}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Display events if available */}
                            {parsedData.events && parsedData.events.length > 0 && parsedData.events[0].content && (
                                <div className="bg-[#111111] rounded-[14px] p-4 border border-[#1A1A1A]">
                                    <h3 className="text-lg font-semibold mb-3 text-[#F5F5F5]">Comprehensive Evaluation</h3>

                                    {parsedData.events[0].content.includes("final_evaluation") && (
                                        <div className="space-y-4 text-[#999999]">
                                            {/* Extract and display the structured evaluation */}
                                            {(() => {
                                                try {
                                                    // Extract the JSON block from the content
                                                    const jsonMatch = parsedData.events[0].content.match(/```json\n([\s\S]*?)\n```/);
                                                    if (jsonMatch && jsonMatch[1]) {
                                                        const evalData = JSON.parse(jsonMatch[1]);

                                                        return (
                                                            <div className="space-y-6 text-[#999999]">
                                                                {/* Offensive/Defensive Skills */}
                                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                    {evalData.skill_assessment && evalData.skill_assessment.offensive && (
                                                                        <div className="bg-[#0A0A0A] p-3 rounded-lg">
                                                                            <h4 className="font-medium mb-2 text-[#F5F5F5]">Offensive Skills</h4>
                                                                            <div className="space-y-2 text-[#999999]">
                                                                                {Object.entries(evalData.skill_assessment.offensive).map(([key, value]) => (
                                                                                    <div key={key} className="flex justify-between">
                                                                                        <span className="text-[#999999] capitalize">{key.replace(/_/g, ' ')}</span>
                                                                                        <span className={getScoreColor(value)}>{value}</span>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    )}

                                                                    {evalData.skill_assessment && evalData.skill_assessment.defensive && (
                                                                        <div className="bg-[#0A0A0A] p-3 rounded-lg">
                                                                            <h4 className="font-medium mb-2 text-[#F5F5F5]">Defensive Skills</h4>
                                                                            <div className="space-y-2 text-[#999999]">
                                                                                {Object.entries(evalData.skill_assessment.defensive).map(([key, value]) => (
                                                                                    <div key={key} className="flex justify-between">
                                                                                        <span className="text-[#999999] capitalize">{key.replace(/_/g, ' ')}</span>
                                                                                        <span className={getScoreColor(value)}>{value}</span>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                {/* Portal Analysis */}
                                                                {evalData.portal_analysis && (
                                                                    <div className="bg-[#0A0A0A] p-3 rounded-lg">
                                                                        <h4 className="font-medium mb-2 text-[#F5F5F5]">Portal Analysis</h4>

                                                                        {evalData.portal_analysis.transfer_history && (
                                                                            <div className="mb-3 text-[#999999]">
                                                                                <span className="font-medium text-[#F5F5F5]">Transfer History: </span>
                                                                                <span className="text-[#999999]">{evalData.portal_analysis.transfer_history}</span>
                                                                            </div>
                                                                        )}

                                                                        {evalData.portal_analysis.red_flags && evalData.portal_analysis.red_flags.length > 0 && (
                                                                            <div className="mb-3 text-[#999999]">
                                                                                <span className="font-medium text-[#F5F5F5]">Red Flags:</span>
                                                                                <ul className="list-disc pl-5 mt-1 text-red-500">
                                                                                    {evalData.portal_analysis.red_flags.map((flag, i) => (
                                                                                        <li key={i}>{flag}</li>
                                                                                    ))}
                                                                                </ul>
                                                                            </div>
                                                                        )}

                                                                        {evalData.portal_analysis.hidden_gems && evalData.portal_analysis.hidden_gems.length > 0 && (
                                                                            <div className="mb-3 text-[#999999]">
                                                                                <span className="font-medium text-[#F5F5F5]">Hidden Gems:</span>
                                                                                <ul className="list-disc pl-5 mt-1 text-green-500">
                                                                                    {evalData.portal_analysis.hidden_gems.map((gem, i) => (
                                                                                        <li key={i}>{gem}</li>
                                                                                    ))}
                                                                                </ul>
                                                                            </div>
                                                                        )}

                                                                        {evalData.portal_analysis.timeline_urgency && (
                                                                            <div className="text-[#999999]">
                                                                                <span className="font-medium text-[#F5F5F5]">Timeline Urgency: </span>
                                                                                <span className="text-[#E8B800]">{evalData.portal_analysis.timeline_urgency}</span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    }
                                                } catch (error) {
                                                    console.error("Error parsing evaluation JSON:", error);
                                                }

                                                // Fallback to display raw content if JSON parsing fails
                                                const cleanedContent = parsedData.events[0].content.replace(/```json[\s\S]*?```/g, '');
                                                return renderMarkdown(cleanedContent);
                                            })()}
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>

            </div>
        </div>
    );
};
export default ViewEvaluationModal;
